import {
  clearGupVisitOutsideProcess,
  clearGupSsoStarted,
  getGupLanding,
  getPendingGupLanding,
  getPortalRedirect,
  markGupSsoFailed,
} from '@/lib/gup';
import { getUserProfile, switchUserProfile } from '@/api/backend/profile';
import { ApiErrorHandler } from '@/components/api-error-handler';
import { FloatingButtons } from '@/components/floating-buttons';
import { Footer } from '@/components/footer';
import { MobileBottomNavigation } from '@/components/mobile-bottom-navigation';
import { NavBar } from '@/components/nav-bar';
import { PageLoading } from '@/components/page-loading';
import { isOidcCallbackUrl } from '@/lib/oidc-callback';
import UserSatisfaction from '@/components/user-satisfaction';
import { PrivacyConsentBanner } from '@/components/privacy-consent-banner';
import { UniversalBackButton } from '@/components/universal-back-button';
import { AuthService } from '@/services/authService';
import { useAuthStore } from '@/store/auth';
import { Breadcrumb, useBreadcrumbsStore } from '@/store/breadcrumbs';
import { useQuery, type QueryClient } from '@tanstack/react-query';
import { createRootRouteWithContext, HeadContent, Outlet, useNavigate, useRouterState } from '@tanstack/react-router';
import { useEffect } from 'react';
import { trackEvent } from '../utils/analytics';
import { usePrivacy } from '../context/PrivacyContext';
import { createPortal } from 'react-dom';

export const Route = createRootRouteWithContext<{
  queryClient: QueryClient;
  authStore: typeof useAuthStore;
  getBreadcrumbData: () => Omit<Breadcrumb, 'path'>;
}>()({
  component: () => (
    <>
      {' '}
      {createPortal(<HeadContent />, document.querySelector('head')!)} <RootComponent />
    </>
  ),
  notFoundComponent: () => <p>Not Found</p>,
  head: () => {
    return {
      meta: [
        {
          title: 'OPAZ E-Service Portal',
        },
      ],
    };
  },
});

function RootComponent() {
  const { hasConsent } = usePrivacy();

  const { setUser, authService } = useAuthStore((state) => state);
  const matches = useRouterState({ select: (s) => s.matches });
  const currentPath = useRouterState({
    select: (s) => s.resolvedLocation?.pathname ?? s.location.pathname,
  });
  const searchStr = useRouterState({ select: (s) => s.location.searchStr });
  // Back from Keycloak (e.g. THEQA from a Gov.om link): wait for the callback instead of flashing the home page.
  const isOidcCallback = isOidcCallbackUrl(searchStr, !!authService);
  const navigate = useNavigate();

  const setBreadcrumbs = useBreadcrumbsStore((state) => state.setBreadcrumbs);
  const idToTextMap = useBreadcrumbsStore((state) => state.idToTextMap);

  const authPages = ['/login', '/signup', '/forgot-password', '/opazpublic', '/verify-email', '/start-service'];

  const hideLayout =
    authPages.some((page) => currentPath === page || currentPath === page + '/') ||
    !!currentPath.match(/\/processes\/.*\/task\/.*/);

  const publicPages = ['/', '/about-us', '/accessibility', '/contact-us', '/faq', '/services', '/sitemap', '/chat-bot'];
  const showSatisfaction = publicPages.some((path) => currentPath === path || currentPath.startsWith(path + '/'));

  // Handle OIDC callback
  useEffect(() => {
    const handleOidcCallback = async () => {
      const urlParams = new URLSearchParams(window.location.search);

      // Check if this is an OIDC callback (presence of 'code' and 'state' parameters)
      // An IdP error comes back as 'error' instead of 'code'; signinCallback rejects it like any other failed login.
      if ((urlParams.has('code') || urlParams.has('error')) && urlParams.has('state') && authService) {
        try {
          const user = await authService.signinCallback();
          clearGupSsoStarted();
          // Clear the URL parameters and go to the page that required the login (or the dashboard)
          const returnTo = getPortalRedirect(AuthService.getReturnTo(user));
          await navigate({ to: getPendingGupLanding() ?? returnTo ?? '/dashboard', replace: true });
        } catch (error) {
          void error; // OIDC errors may contain the return URL; never log GUP ciphertext.
          const landing = getPendingGupLanding();
          if (landing) markGupSsoFailed();
          navigate({ to: '/login', search: { redirect: landing }, replace: true });
        }
      }
    };

    handleOidcCallback();
  }, [authService, navigate]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (!((params.has('code') || params.has('error')) && params.has('state'))) clearGupVisitOutsideProcess(currentPath);
  }, [currentPath]);

  // Landing preparation loads/switches the profile before the application mounts.
  const isGupLanding = !!getGupLanding(window.location.pathname + window.location.search);
  const { isLoading } = useQuery({
    queryKey: ['userProfile'],
    queryFn: async () => {
      const urlSearchParams = new URLSearchParams(window.location.search);
      const paramsEntries = Object.fromEntries(urlSearchParams.entries());
      const { crNumber, ...restParamsEntries } = paramsEntries;

      const profileData = await getUserProfile();

      if (crNumber) {
        const otherUserProfile = profileData.profiles.find((profile) => profile.identifier === crNumber);

        if (otherUserProfile) {
          await switchUserProfile(otherUserProfile.id);

          const otherProfileData = {
            activeProfileId: otherUserProfile.id,
            profiles: profileData.profiles,
          };

          setUser(otherProfileData);

          const restSearchParams = new URLSearchParams(restParamsEntries).toString();
          window.history.replaceState(
            null,
            '',
            `${window.location.pathname}${restSearchParams ? `?${restSearchParams}` : ''}`,
          );
          return otherProfileData;
        }
      }

      setUser(profileData);
      return profileData;
    },
    enabled:
      !AuthService.isTokenExpired() &&
      !isOidcCallback &&
      !isGupLanding &&
      !(currentPath === '/signup' && !!getPendingGupLanding()),
  });

  useEffect(() => {
    const breadcrumbs = matches
      .filter((match) => match.context.getBreadcrumbData)
      .map(({ pathname, context }) => {
        const { id, translationKey } = context.getBreadcrumbData();

        return {
          id,
          translationKey: id ? idToTextMap[id] : translationKey,
          path: pathname,
        };
      })
      .filter((breadcrumb) => breadcrumb.id || breadcrumb.translationKey);

    setBreadcrumbs(breadcrumbs);
  }, [matches, setBreadcrumbs, idToTextMap]);

  // Track page views and performance
  useEffect(() => {
    if (hasConsent) {
      const startTime = performance.now();
      const lastMatch = matches[matches.length - 1];
      const currentPath = lastMatch.pathname;

      trackEvent('page_view', 'Page Interaction', {
        page_path: currentPath,
      });

      // Track slow page loads (threshold: 3 seconds)
      const measurePageLoad = () => {
        const loadTime = performance.now() - startTime;
        if (loadTime > 3000) {
          // Call trackEvent directly to use current pathname
          trackEvent('slow_page', 'Performance', {
            page_path: currentPath,
            load_time: Math.round(loadTime),
          });
        }
      };

      // Measure after page is fully loaded
      if (document.readyState === 'complete') {
        measurePageLoad();
      } else {
        window.addEventListener('load', measurePageLoad, { once: true });
        return () => window.removeEventListener('load', measurePageLoad);
      }
    }
  }, [matches, hasConsent]);

  return (
    <>
      <ApiErrorHandler />

      {isLoading || isOidcCallback ? (
        <PageLoading showNav />
      ) : (
        <div className="flex min-h-screen flex-col pb-20 md:pb-0">
          {!hideLayout && <NavBar />}
          {!hideLayout && currentPath !== '/' && <UniversalBackButton />}
          <div className="flex-1">
            <Outlet />
          </div>
          {showSatisfaction && <FloatingButtons />}
          {showSatisfaction && <UserSatisfaction />}
          {!hideLayout && <Footer />}
          {!hideLayout && <MobileBottomNavigation />}
          <PrivacyConsentBanner />
        </div>
      )}
    </>
  );
}
